import Vue from 'vue'
import Router, { isNavigationFailure, NavigationFailureType } from 'vue-router'

// Routes
import AppsRoutes from './apps.routes'
import PagesRoutes from './pages.routes'
import UsersRoutes from './users.routes'
import LandingRoutes from './landing.routes'

import ClientRoutes from './client.routes'
import RolesRoutes from './roles.routes'
import MantenedorRoutes from './mantenedor.routes'
import TicketsRoutes from './tickets.routes'
import CategoriasRoutes from './categorias.routes'
import ServidoresRoutes from './servidores.routes'
import CamposRoutes from './campos.routes'
import PeriodicidadesRoutes from './periodicidades.routes'
import TarifasManage2Routes from './tarifas-manage2.routes'
import ContratosRoutes from './contratos.routes'
import IncidenciasMasivasRoutes from './incidencias-masivas.routes'
import OrdenesTrabajoRoutes from './ordenes-trabajo.routes'
import ChatwootRoutes from './chatwoot.routes'
import ClientesAdministradosRoutes from './clientes-administrados.routes'

import store from '../store/index';
import { recoverAndReload } from '../utils/chunkReloadGuard';


Vue.use(Router)

export const routes = [{
        path: '/',
        name: 'dashboard',
        component: () =>
        import ( /* webpackChunkName: "dashboard" */ '@/pages/dashboard/DashboardPage.vue'),
        meta: { requiresAuth: true }
    },
    ...AppsRoutes,
    ...PagesRoutes,
    ...UsersRoutes,
    ...LandingRoutes,
    ...ClientRoutes,
    ...RolesRoutes,
    ...MantenedorRoutes,
    ...TicketsRoutes,
    ...CategoriasRoutes,
    ...ServidoresRoutes,
    ...CamposRoutes,
    ...PeriodicidadesRoutes,
    ...TarifasManage2Routes,
    ...ContratosRoutes,
    ...IncidenciasMasivasRoutes,
    ...OrdenesTrabajoRoutes,
    ...ChatwootRoutes,
    ...ClientesAdministradosRoutes,
    {
        path: '/blank',
        name: 'blank',
        component: () =>
            import ( /* webpackChunkName: "blank" */ '@/pages/BlankPage.vue')
    },
    {
        path: '*',
        name: 'error',
        component: () =>
            import ( /* webpackChunkName: "error" */ '@/pages/error/NotFoundPage.vue'),
        meta: {
            layout: 'error'
        }
    }
]

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL || '/',
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition

        return { x: 0, y: 0 }
    },
    routes
})

// Los fallos de navegación "benignos" ocurren cuando un guard redirige la
// navegación (p. ej. la inyección de contexto de cliente que agrega `?cliente=`
// a /apps/board) o cuando se navega a la ruta actual. En vue-router 3.1+ estos
// casos rechazan la promesa de push/replace; si nadie los captura terminan como
// excepciones no controladas (ruido en Sentry). Los ignoramos: la navegación sí
// se completó (al destino redirigido o a la ruta ya activa).
function isBenignNavigationFailure(err) {
    if (typeof isNavigationFailure === 'function' && NavigationFailureType) {
        if (isNavigationFailure(err, NavigationFailureType.redirected) ||
            isNavigationFailure(err, NavigationFailureType.duplicated)) {
            return true
        }
    }
    // Fallback por si la versión instalada no expone los helpers.
    return !!err && err.name === 'NavigationDuplicated'
}

const originalPush = Router.prototype.push
Router.prototype.push = function push(location) {
    return originalPush.call(this, location).catch((err) => {
        if (!isBenignNavigationFailure(err)) throw err
    })
}

const originalReplace = Router.prototype.replace
Router.prototype.replace = function replace(location) {
    return originalReplace.call(this, location).catch((err) => {
        if (!isBenignNavigationFailure(err)) throw err
    })
}

// Red de seguridad final. El override de push/replace de arriba sólo cubre las
// navegaciones que pasan por `Router.prototype`. Algunos orígenes disparan la
// navegación en "modo promesa" sin capturar el rechazo (p. ej. el propio
// `<router-link>`/`v-btn :to` de Vuetify al redirigir un guard, o helpers que
// llaman a `push` sin `.catch`), y el fallo benigno "Redirected ... via a
// navigation guard" termina como `Uncaught (in promise)` en consola.
// Lo silenciamos a nivel de `window` sólo cuando es un fallo de navegación
// benigno; cualquier otro rechazo se deja pasar intacto.
if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
        if (isBenignNavigationFailure(event.reason)) {
            event.preventDefault()
        }
    })
}

// vue-router 3 expone los fallos de import() de componentes lazy vía onError.
// Tras un despliegue en Cloudflare Pages, un chunk viejo puede haber
// desaparecido; delegamos en la recuperación de una sola pasada.
router.onError((error) => {
    if (/Loading( CSS)? chunk [\w-]+ failed/i.test(error.message) ||
        /Loading chunk .* failed/i.test(error.message)) {
        recoverAndReload()
    }
})

/**
 * Before each route update
 */
router.beforeEach((to, from, next) => {
    if (to.matched.some(record => record.meta.requiresAuth)) {
        // Verificar si el usuario está autenticado
        if (!isLoggedIn()) {
          // Redirigir al inicio de sesión si no está autenticado
           return next('/auth/signin')
        }
        else if(!isTwoFa()){
            return next('/view2fa');
        }
        else {
          // Continuar si está autenticado
          return next()
        }
      } else {
        // Continuar para rutas públicas
        return next()
      }
})

/**
 * Client context injection: when a client is selected, inject filter params
 * into supported routes so each page auto-filters for that client.
 */
const CLIENT_CONTEXT_ROUTES = [
  { test: (p) => p.startsWith('/apps/servicios'),         buildQuery: (c) => ({ search: c.rut, searchType: 'cliente_rut' }) },
  { test: (p) => p.startsWith('/notas-cobros'),           buildQuery: (c) => ({ search_by: 'rut', search: c.rut }) },
  { test: (p) => p.startsWith('/apps/board'),             buildQuery: (c) => ({ cliente: c.rut }) },
  { test: (p) => p.startsWith('/sofono'),                 buildQuery: (c) => ({ cliente: c.rut }) },
  { test: (p) => p.startsWith('/cotizaciones'),           buildQuery: (c) => ({ cliente: c.rut }) },
  { test: (p) => p.startsWith('/dashboardClient_search'), buildQuery: (c) => ({ search: c.rut }) },
  { test: (p) => p.startsWith('/resumen-cliente'),        buildQuery: (c) => ({ rut: c.rut }) },
];

router.beforeEach((to, from, next) => {
  const client = store.state.clientContext && store.state.clientContext.selectedClient;
  if (client) {
    const match = CLIENT_CONTEXT_ROUTES.find((r) => r.test(to.path));
    if (match) {
      const ctx = match.buildQuery(client);
      const allPresent = Object.entries(ctx).every(([k, v]) => to.query[k] === v);
      if (!allPresent) {
        return next({ path: to.path, query: { ...to.query, ...ctx } });
      }
    }
  }
  return next();
});

/**
 * After each route update
 */
router.afterEach((to, from) => {})



// Función para verificar si el usuario está autenticado
function isLoggedIn() {
    if(!store.getters.isAuthenticated.access_token){
       return false;
    }else{
        return true
    }


  }
  
  // Función para verificar si el usuario tiene 2fa activo o no
  function isTwoFa() {
    if (process.env.VUE_APP_DISABLE_2FA === 'true') {
        return true;
    }
    if( store.getters.isAuthenticated.access_token && store.getters.twoFAError == true){
        return false;
    }else{
        return true
    }
  }


export default router
